/**
 * MedicalSection Component
 * Displays U-Untersuchung info, normal behaviors, and red flags
 */

import { Stethoscope } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { BabyDevelopmentMedical } from '../types';

interface MedicalSectionProps {
  medical: BabyDevelopmentMedical | null;
  isLoading?: boolean;
}

export function MedicalSection({ medical, isLoading }: MedicalSectionProps) {
  const { t } = useTranslation('babyDevelopment');
  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Stethoscope className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('medical.title')}</h3>
        </div>
        <div className="space-y-3">
          <div className="h-24 bg-white/10 rounded-[15px] animate-pulse" />
          <div className="h-24 bg-white/10 rounded-[15px] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!medical) {
    return null;
  }

  const hasUExam = medical.u_exam_name && medical.u_exam_timeframe;
  const hasNormal = medical.normal_behaviors && medical.normal_behaviors.length > 0;
  const hasRedFlags = medical.red_flags && medical.red_flags.length > 0;

  if (!hasUExam && !hasNormal && !hasRedFlags) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Stethoscope className="w-5 h-5 text-white" />
        <h3 className="text-white text-xl font-bold">{t('medical.title')}</h3>
      </div>

      <div className="space-y-3">
        {/* U-Untersuchung */}
        {hasUExam && (
          <div className="bg-white/10 rounded-[15px] p-4">
            <h4 className="text-white font-medium">
              {t('medical.examination', { name: medical.u_exam_name })}
            </h4>
            <p className="text-white/60 text-sm mt-1">{medical.u_exam_timeframe}</p>

            {medical.u_exam_checks && medical.u_exam_checks.length > 0 && (
              <div className="mt-3">
                <p className="text-white/50 text-xs uppercase tracking-wide mb-2">
                  {t('medical.checksTitle')}
                </p>
                <ul className="space-y-1">
                  {medical.u_exam_checks.map((check, index) => (
                    <li key={index} className="flex items-center gap-2 text-white/70 text-sm">
                      <span className="text-blue-400">•</span>
                      {check}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Normal Behaviors */}
        {hasNormal && (
          <div className="bg-white/10 rounded-[15px] p-4">
            <h4 className="text-green-400 font-medium mb-2">{t('medical.normal')}</h4>
            <ul className="space-y-1">
              {medical.normal_behaviors.map((behavior, index) => (
                <li key={index} className="flex items-center gap-2 text-white/70 text-sm">
                  <span className="text-green-400">•</span>
                  {behavior}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Red Flags */}
        {hasRedFlags && (
          <div className="bg-white/10 rounded-[15px] p-4">
            <h4 className="text-red-400 font-medium mb-2">{t('medical.redFlags')}</h4>
            <ul className="space-y-1">
              {medical.red_flags.map((flag, index) => (
                <li key={index} className="flex items-center gap-2 text-white/70 text-sm">
                  <span className="text-red-400">•</span>
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
